import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, Image, ProgressBar, useWindowDimensions, ScrollView, Keyboard, TouchableOpacity } from 'react-native-web';
import SwipeableViews from 'react-swipeable-views';
import * as Animatable from 'react-native-animatable';
import toast from 'react-hot-toast';
import FeatherIcon from 'react-native-vector-icons/dist/Feather';
import { cloneDeep } from 'lodash';
import imageCompression from 'browser-image-compression';

// Child Components
import TabSearch from './tab_search';
import TabTruckInfo from './tab_truck_info';

// Custom Component
import { Button, NavBar, TextBox } from '../../components';
import ServiceAgreementModal from '../../components/component_service_agreement';

// Assets
import { ReactComponent as ProfileDoneImage } from '../../images/svg_profile_done.svg';

// Utils
import {
    checkEmail,
    checkPhoneNumber,
    getData,
    removeData,
    showAlert,
    showAppError,
    storeData,
    validateUSNumber
} from '../../utils/util_helpers';

// APIs
import { checkVendorEmailExist, registerCustom, registerNormal, saveTempTruckProfile } from '../../modules';
import { zeroBounceValidate } from '../../modules/api_external';
import { sendSlackMessageSimple } from '../../modules/api_slack';

// Root Navigation
import * as RootNavigation from '../../navigation/RootNavigation';
import RouteKeys from '../../navigation/RouteKeys';
import routerKeys from '../../navigation/RouteKeys';

// Configs
import GLOBAL from '../../configs/config_global';
import DATA from '../../configs/config_data';
import EMAIL_VALIDATION from '../../configs/config_email_validations';
import { getGlobalState, STATE_KEYS } from '../../configs/config_state';
import LoadingView from '../../components/component_loading_view';

export function Register() {
    const { width, height } = useWindowDimensions();
    const TEMP_REGISTRATION_DATA_KEY = 'tempRegistrationData';

    // region Refs
    const totalStep = useRef(4);

    // Step 1
    const accountInfoScroll = useRef(null);
    const txtEmail = useRef(null);
    const txtPassword = useRef(null);
    const txtContactName = useRef(null);
    const txtFoodTruckName = useRef(null);
    const txtPhone = useRef(null);

    // Step 2
    const searchTab = useRef(null);
    const selectedTruckID = useRef(null);

    // endregion

    // region Local State
    const [currentStep, setCurrentStep] = useState(1);
    const [creatingProfile, setCreatingProfile] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const [layout, setLayout] = useState({ width, height });

    // Step 1
    const [email, setEmail] = useState('');
    const [contactName, setContactName] = useState('');
    const [foodTruckName, setFoodTruckName] = useState('');
    const [phone, setPhone] = useState('');
    const [phoneOptIn, setPhoneOptIn] = useState(true);
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [checkingExistingEmail, setCheckingExistingEmail] = useState(false);
    const [emailValidationResult, setEmailValidationResult] = useState(null);
    const [loadingEmailValidation, setLoadingEmailValidation] = useState(false);

    // Step 2 (Manual)
    const [website, setWebsite] = useState('');
    const [cuisineList, setCuisineList] = useState([]);
    const [selectedCities, setSelectedCities] = useState([]);
    const [licenseNeededCities, setLicenseNeededCities] = useState([]);
    const [logoPhoto, setLogoPhoto] = useState(null);
    const [coverPhoto, setCoverPhoto] = useState(null);

    // endregion

    // region Component Mounting
    useEffect(() => {
        setTimeout(() => {
            // Check if there was temp registration data saved to local storage
            getData(TEMP_REGISTRATION_DATA_KEY)
                .then((result) => {
                    if (result) {
                        showAlert({ text: 'We detected saved registration data from a previous session and pre-populated them for your convenience' });
                        const savedState = JSON.parse(result || '{}');

                        // Set to state
                        setContactName(savedState.contactName);
                        setFoodTruckName(savedState.foodTruckName);
                        setEmail(savedState.email);
                        setPhone(savedState.phone);
                        setCreatingProfile(savedState.creatingProfile);
                        setWebsite(savedState.website);
                        setCuisineList(savedState.cuisineList);
                        setSelectedCities(savedState.selectedCities);
                        setLicenseNeededCities(savedState.licenseNeededCities);
                    }
                });
        }, 500);
    }, []);
    // endregion

    // region Function - Go To Step
    const goToStep = (step) => {
        setCurrentStep(step);
    }
    // endregion

    // region Function - Save Current State
    const saveCurrentState = () => {
        const savingObj = {
            contactName,
            foodTruckName,
            email,
            phone,
            creatingProfile,
            website,
            cuisineList,
            selectedCities,
            licenseNeededCities
        };

        storeData(TEMP_REGISTRATION_DATA_KEY, JSON.stringify(savingObj))
            .then(() => console.log('[INFO] Temp Registration Data Saved', savingObj));
    }
    // endregion

    // region Function - Validate Email via ZeroBounce
    const getOtherEmailErrorStr = (validationResult = null) => {
        const result = validationResult || emailValidationResult;
        return(`We could not validate your email address, please use another email address to sign up.<br/><br/>You can also reach out to our team at vendor.support@gotruckster.com if you believe this is a mistake, please provide us with this data: "${result?.['status']} - ${result?.['sub_status']}".`);
    }

    const showEmailValidationResult = (result) => {
        // Check if email failed validation
        if (result['status'] !== 'valid') {
            // If this is just a typo error
            if (result['sub_status'] === 'possible_typo') {
                showAlert({
                    text: `Did you mean "${result['did_you_mean']}"?`,
                    confirmButton: {
                        text: 'Yes',
                        color: GLOBAL.COLOR.MAIN,
                        onPress: () => {
                            const tempObj = cloneDeep(emailValidationResult);
                            tempObj['status'] = 'valid';
                            tempObj['address'] = result['did_you_mean'];
                            setEmail(result['did_you_mean']);
                            setEmailValidationResult(tempObj);
                        }
                    },
                    cancelButton: { text: 'Let me check' }
                });
            }
            else if (result['sub_status'] === 'mailbox_not_found') {
                showAlert({ text: 'We could not reach your mailbox, please re-check your email address to see if it is misspelled.' });
            }
            else showAlert({ text: getOtherEmailErrorStr(result) });
        }
    }

    const goValidateEmail = (email = '') => {
        let typoList = Object.keys(EMAIL_VALIDATION.possibleTypos),
            typoItem = typoList.find((typo) => email?.trim().includes(typo));

        if (typoItem) {
            let fixedEmail = email.trim();
            typoList.forEach((typo) => fixedEmail = fixedEmail.replace(typo, EMAIL_VALIDATION.possibleTypos[typo]));

            const resultObj = {
                address: email,
                status: 'invalid',
                sub_status: 'possible_typo',
                did_you_mean: fixedEmail
            };

            setEmailValidationResult(resultObj);
            showEmailValidationResult(resultObj);
            return;
        }

        setLoadingEmailValidation(true);
        zeroBounceValidate(email)
            .then((result) => {
                setEmailValidationResult(result);

                // Override check for certain emails
                const vendorEmail = email?.trim().toLowerCase();
                if (
                    result['sub_status']?.includes('role_based') ||
                    result['status']?.includes('catch-all') ||
                    !!result['free_email'] ||
                    DATA.overrideVendorEmails.includes(vendorEmail)
                ) {
                    goToStep(2);
                    return;
                }

                // Check if email failed validation
                if (result['status'] !== 'valid') {
                    showEmailValidationResult(result);

                    // Notify Slack
                    sendSlackMessageSimple(GLOBAL.SLACK_HOOKS.ZERO_BOUNCE, 'ZeroBounce Error Received', result['sub_status'] + '<br/><br/>' + JSON.stringify(result));
                }
                else goToStep(2);
            })
            .catch((error) => {
                // Notify Slack
                sendSlackMessageSimple(GLOBAL.SLACK_HOOKS.ZERO_BOUNCE, 'ZeroBounce API Failed - Verification Skipped', `Email Address: ${email}<br/><br/>` + (typeof error === 'string' ? error : error.errors?.status));

                // Skip verification
                goToStep(2);
            })
            .finally(() => setLoadingEmailValidation(false))
    }
    // endregion

    // region Function - Go To Search Tab
    const goToSearchTab = () => {
        if (!checkEmail(email) || !checkPhoneNumber(phone)) {
            toast.error('Please make sure all information is valid');
            return;
        }

        // Save temp data to local storage in case of crash
        saveCurrentState();

        // Check if vendor email exist first
        Keyboard.dismiss();
        setCheckingExistingEmail(true);
        checkVendorEmailExist(email)
            .then(() => {
                // Save to temp vendor table
                saveTempTruckProfile({
                    email,
                    phone,
                    contact_name: contactName,
                    name: foodTruckName
                });

                // Validate Email Address
                if (getGlobalState(STATE_KEYS.IS_DEV)) {
                    goToStep(2);
                    return;
                }

                // Check email's validity
                if (email?.trim().toLowerCase() === emailValidationResult?.['address']?.trim()?.toLowerCase()) {
                    if (emailValidationResult?.['status'] === 'valid') {
                        goToStep(2);
                    }
                    else showAlert({ text: getOtherEmailErrorStr() });
                }
                else goValidateEmail(email);
            })
            .catch((error) => {
                if (error === 'Already Existed') {
                    showAlert({ text: 'The email you entered already belonged to another Vendor user, please select another email' });
                }
                else showAppError(error);
            })
            .finally(() => setCheckingExistingEmail(false))
    }
    // endregion

    // region Function - Select Truck
    const selectTruck = (truckData = {}) => {
        showAlert({
            text: `Do you confirm that this is your truck ("${truckData.name}")? You will NOT be able to change it later. This is a one time pick, please select carefully.`,
            confirmButton: {
                text: 'Yes',
                color: GLOBAL.COLOR.MAIN,
                onPress: () => {
                    selectedTruckID.current = truckData.id;
                    goToStep(3);
                }
            },
            cancelButton: { text: 'No' }
        });
    };
    // endregion

    // region Function - Go Sign Up
    const goSignUp = (truckID) => {
        setLoading(true);
        setTimeout(() => {
            if (truckID) {
                registerNormal(contactName, email, password, phone, phoneOptIn ? 1 : 0, truckID)
                    .then(() => {
                        removeData(TEMP_REGISTRATION_DATA_KEY);
                        goToStep(4);
                    })
                    .catch((error) => showAppError(error))
                    .finally(() => setLoading(false));
            }
            else {
                // Compress logo and cover photo
                const imageCompressionOptions = { maxSizeMB: 1, maxWidthOrHeight: 1920, useWebWorker: true };
                Promise.all([imageCompression(logoPhoto, imageCompressionOptions), imageCompression(coverPhoto, imageCompressionOptions)])
                    .then((compressResult) => {
                        const sendObj = {
                            'contact_name': contactName,
                            'phone': phone,
                            'phone_opt_in': phoneOptIn ? 1 : 0,
                            'email': email,
                            'password': password,
                            'password_confirmation': password,

                            'logo': compressResult[0],
                            'cover_photo': compressResult[1],
                            'name': foodTruckName,
                            'truck_email': email,
                            'cuisine': cuisineList.map((item) => item.id).join(','),
                            'contact_phone': phone,
                            'company_description': '',
                            'description': '',
                            'website_url': website,
                            'facebook_url': '',
                            'instagram_url': '',
                            'twitter_url': '',
                            'city': selectedCities.join(','),

                            // To be parsed in API call
                            'licenseNumberArr': licenseNeededCities.map((city) => ({ id: city.id, value: city.value }))
                        };

                        // Call API
                        registerCustom(sendObj)
                            .then(() => {
                                removeData(TEMP_REGISTRATION_DATA_KEY);
                                goToStep(4);
                            })
                            .catch((error) => showAppError(error))
                            .finally(() => setTimeout(() => setLoading(false), 1000));
                    })
                    .catch((error) => showAppError(error.message));
            }
        }, 2000);
    }
    // endregion

    // region Render Account Info Tab
    const renderAccountInfoTab = () => (
        <ScrollView
            key={'accountInfo'}
            ref={accountInfoScroll}
            style={{ flex: 1, width: '100%' }}
            contentContainerStyle={{ paddingHorizontal: '30%', alignItems: 'center', paddingBottom: 128 }}>
            {/* Contact Input */}
            <TextBox
                key={'txtContactName'}
                ref={txtContactName}
                title={'Contact Name'}
                placeholder={'Contact Name'}
                returnKeyType={'next'}
                autoCapitalize={'words'}
                onSubmitEditing={() => txtFoodTruckName.current?.focus()}
                onChangeText={setContactName}
                value={contactName}
            />

            {/* Food Truck Name Input */}
            <TextBox
                key={'txtFoodTruckName'}
                ref={txtFoodTruckName}
                title={'Food Truck Name'}
                placeholder={'Food Truck Name'}
                returnKeyType={'next'}
                autoCapitalize={'words'}
                onSubmitEditing={() => txtPhone.current?.focus()}
                onChangeText={setFoodTruckName}
                value={foodTruckName}
            />

            {/* Phone Input */}
            <TextBox
                key={'txtPhone'}
                ref={txtPhone}
                title={'Phone'}
                placeholder={'Phone'}
                returnKeyType={'next'}
                autoCapitalize={'none'}
                keyboardType={'number-pad'}
                onSubmitEditing={() => txtEmail.current?.focus()}
                onChangeText={setPhone}
                value={phone}
                useMaskedInput={true}
                showError={phone?.trim() ? !validateUSNumber(phone) : false}
            />

            {/* Phone Number Opt-in */}
            <TouchableOpacity
                onPress={() => setPhoneOptIn(!phoneOptIn)}
                style={{ flexDirection: 'row', alignItems: 'center', marginTop: 16, marginBottom: 8, alignSelf: 'flex-start' }}>
                <View style={{
                    borderWidth: 2, borderRadius: 4, borderColor: phoneOptIn ? GLOBAL.COLOR.MAIN : GLOBAL.COLOR.TEXT_GRAY,
                    backgroundColor: phoneOptIn ? GLOBAL.COLOR.MAIN : 'white', height: 20, width: 20,
                    alignItems: 'center', justifyContent: 'center'
                }}>
                    <FeatherIcon name={'check'} color={'white'} size={16} />
                </View>

                <Text style={{ color: GLOBAL.COLOR.BASIC.NEUTRAL_DARK_GREY, fontSize: 14, flex: 1, marginLeft: 8 }}>
                    Receive catering and account updates via text.
                </Text>
            </TouchableOpacity>

            {
                phoneOptIn &&
                <View style={{ flexDirection: 'row', alignItems: 'flex-start', marginVertical: 12, alignSelf: 'flex-start' }}>
                    <FeatherIcon
                        name={'info'}
                        size={18}
                        color={GLOBAL.COLOR.BASIC.NEUTRAL_LIGHT_GREY}
                    />

                    <Text style={{ color: GLOBAL.COLOR.BASIC.NEUTRAL_LIGHT_GREY, fontSize: 14, flex: 1, marginLeft: 8, lineHeight: 20 }}>
                        Truckster Vendor SMS. Message frequency varies. Message and data rates may apply. Reply HELP for help and STOP to cancel.{' '}
                    </Text>
                </View>
            }

            {/* Email Input */}
            <TextBox
                key={'txtEmail'}
                ref={txtEmail}
                title={'Email'}
                placeholder={'Email'}
                returnKeyType={'next'}
                autoCapitalize={'none'}
                keyboardType={'email-address'}
                onSubmitEditing={() => txtPassword.current?.focus()}
                onChangeText={setEmail}
                value={email}
            />

            {/* Password Input */}
            <TextBox
                key={'txtPassword'}
                ref={txtPassword}
                containerStyle={{ marginTop: 8 }}
                title={'Password'}
                placeholder={'Password'}
                returnKeyType={'done'}
                autoCapitalize={'none'}
                secureTextEntry={!showPassword}
                showHintButton={true}
                hintButtonImage={'remove-red-eye'}
                hintButtonOpacity={showPassword ? 1 : 0.5}
                hintButtonColor={GLOBAL.COLOR.TEXT_GRAY}
                hintButtonOnPress={() => setShowPassword(!showPassword)}
                onChangeText={setPassword}
                value={password}
                showPasswordValidator={true}
                onSubmitEditing={() => goToSearchTab()}
            />

            {/* Terms Text */}
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', padding: 8, marginTop: 8 }}>
                {/* Text */}
                <Text onPress={() => accountInfoScroll.current?.scrollTo({ y: 0 })} style={{ color: GLOBAL.COLOR.TEXT_GRAY, fontSize: 14, textAlign: 'center' }}>
                    By signing up, you agree to Truckster's{' '}

                    {/* Privacy Policy */}
                    <Text
                        onPress={() => window.open('https://gotruckster.com/privacy', '_blank')}
                        style={{ color: GLOBAL.COLOR.TEXT_GRAY, textDecorationLine: 'underline', fontSize: 14, textAlign: 'center' }}>
                        Terms & Conditions
                    </Text>

                    {/* And */}
                    <Text style={{ color: GLOBAL.COLOR.TEXT_GRAY, fontSize: 14, textAlign: 'center' }}>
                        {' '}and{' '}
                    </Text>

                    {/* Privacy Link */}
                    <Text
                        onPress={() => window.open('https://gotruckster.com/privacy', '_blank')}
                        style={{ color: GLOBAL.COLOR.TEXT_GRAY, textDecorationLine: 'underline', fontSize: 14, textAlign: 'center' }}>
                        Privacy Policy
                    </Text>
                </Text>
            </View>

            {/* Submit */}
            <Button
                style={{ marginTop: 12 }}
                text={'Next'}
                isLoading={isLoading || checkingExistingEmail || loadingEmailValidation}
                locked={!email?.trim() || !password?.trim() || !contactName?.trim() || !foodTruckName?.trim() || !phone?.trim()}
                onPress={() => goToSearchTab()}
            />

            {/* Back to Login */}
            <Text style={{ fontWeight: '400', color: GLOBAL.COLOR.BASIC.NEUTRAL_GREY, marginVertical: 24 }}>
                Already have an account?
                <Text onPress={() => RootNavigation.navigate(RouteKeys.LOGIN)} style={{ fontWeight: '500', color: GLOBAL.COLOR.MAIN }}>
                    {'  '}Log in now
                </Text>
            </Text>
        </ScrollView>
    )
    // endregion

    // region Render Search Tab
    const renderSearchTab = () => (
        <TabSearch
            ref={searchTab}
            tabLabel={'Search Truck'}
            initialKeyword={foodTruckName}
            onTruckSelected={selectTruck}
            onNewProfilePressed={() => {
                selectedTruckID.current = null;
                setCreatingProfile(true);
                goToStep(2);
            }}
        />
    );
    // endregion

    // region Render Truck Info
    const renderTruckInfo = () => (
        <TabTruckInfo
            key={'truckInfoPage'}
            isLoading={isLoading}
            truckCuisineList={cuisineList}
            onCuisineSelected={setCuisineList}
            selectedCities={selectedCities}
            onCitiesSelected={setSelectedCities}
            truckWebsite={website}
            onTruckWebsiteChanged={setWebsite}
            logoPhoto={logoPhoto}
            onLogoChanged={setLogoPhoto}
            coverPhoto={coverPhoto}
            onCoverChanged={setCoverPhoto}
            onNextButtonPressed={() => {
                if (
                    !cuisineList.length ||
                    !selectedCities.length ||
                    !website ||
                    !logoPhoto ||
                    !coverPhoto ||
                    (
                        !!licenseNeededCities.length &&
                        !!licenseNeededCities.filter((city) => !city.value?.trim()).length
                    )
                ) {
                    showAlert({ text: 'Please fill in all required information and select your brand photos before continuing' });
                }
                else goToStep(3);
            }}
            saveTempDataFunc={() => saveCurrentState()}
            licenseNeededCities={licenseNeededCities}
            onLicenseNeededCitiesUpdated={setLicenseNeededCities}
            onSwitchToSearchPressed={() => setCreatingProfile(false)}
        />
    );
    // endregion

    // region Render Register Complete View
    const renderRegisterCompleteView = () => (
        <View key={'completePage'} style={{ flex: 1, backgroundColor: 'white', alignItems: 'center', justifyContent: 'center', paddingVertical: 32, marginHorizontal: '30%' }}>
            {/* Main Content */}
            <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
                {/* Check Image */}
                <View style={{ marginTop: -80 }}>
                    <ProfileDoneImage width={180} height={200}/>
                </View>

                {/* Title */}
                <Text style={{ fontWeight: '900', fontSize: 30, marginTop: 24, marginBottom: 36, color: GLOBAL.COLOR.BASIC.BLACK_TWO, textAlign: 'center' }}>
                    Nice! You've successfully created your profile.
                </Text>

                {/* Description */}
                <Text style={{ color: GLOBAL.COLOR.BASIC.NEUTRAL_DARK_GREY, fontSize: 18, lineHeight: 30, textAlign: 'left' }}>
                    • The Truckster Team is reviewing your account.{'\n'}
                    • You will receive a confirmation email once you're approved and your profile is live, usually within 24-48 hours.
                    {!selectedTruckID.current ? '\n• In the meantime, spruce up your profile! Add your menu, food photos, schedule, and unique description of you and your truck.' : ''}
                </Text>
            </View>

            {/* Done Button */}
            <Button
                style={{ marginTop: 24 }}
                onPress={() => {
                    RootNavigation.goBack();
                    RootNavigation.navigate(routerKeys.LOGIN, { email, password });
                }}
                backgroundColor={GLOBAL.COLOR.MAIN}
                text={'Got it!'}
                textColor={'white'}
            />
        </View>
    )
    // endregion

    // region Watcher - Auto Fill Search Text
    useEffect(() => {
        if (currentStep === 2 && !creatingProfile) {
            searchTab.current?.setKeyword(foodTruckName);
            setTimeout(() => searchTab.current?.goSearchTruck(foodTruckName), 500);
        }
    }, [currentStep, creatingProfile, foodTruckName]);
    // endregion

    // region MAIN RENDER
    const logoWidth = 220;
    const currentProgress = useMemo(() => Number((currentStep / totalStep.current).toFixed(2)), [currentStep]);
    return(
        <View style={{ flex: 1, width: '100%', alignItems: 'center', paddingTop: 64, backgroundColor: '#FFF' }}>
            {/* Progress */}
            <View style={{ width: '100%', flexDirection: 'row', alignItems: 'center' }}>
                <ProgressBar
                    progress={currentProgress}
                    color={'black'}
                    trackColor={GLOBAL.COLOR.INNER_GRAY}
                    style={{ width: '100%', height: 16 }}
                />

                <Text style={{ color: 'white', position: 'absolute', left: (currentStep * (width / totalStep.current)) - (currentStep === totalStep.current ? 32 : 28), fontSize: 12 }}>
                    {(currentProgress * 100)}%
                </Text>
            </View>

            {/* Nav Bar */}
            <NavBar
                hideBorder={true}
                leftButtonIconName={'keyboard-arrow-left'}
                leftButtonIconSize={32}
                leftButtonOnPress={() => {
                    if (currentStep > 1) {
                        goToStep(currentStep - 1);
                    }
                    else RootNavigation.goBack();
                }}
            />

            {/* Logo */}
            <Image
                source={require('../../images/logo_vendor_landscape.png')}
                style={{ width: logoWidth, height: (34 / 156) * logoWidth }}
            />

            {/* Main Content */}
            <View style={{ flex: 1, width: '100%', alignSelf: 'center', alignItems: 'center', paddingTop: 24 }}>
                {/* Welcome Text */}
                {
                    (currentStep < totalStep.current) &&
                    <Animatable.Text animation={'fadeIn'} style={{ fontSize: 40, fontWeight: '900', textAlign: 'center', marginBottom: 36 }}>
                        Welcome to Truckster Vendor
                    </Animatable.Text>
                }

                {/* Main Content */}
                <View style={{ flex: 1, width: '100%' }} onLayout={(e) => setLayout(e.nativeEvent.layout)}>
                    <SwipeableViews
                        style={{ flex: 1, width: '100%' }}
                        containerStyle={{ width: layout.width, height: layout.height }}
                        index={currentStep - 1}
                        onChangeIndex={(i) => setCurrentStep(i + 1)}
                        enableMouseEvents={false}>
                        {/* Account Info */}
                        {renderAccountInfoTab()}

                        {/* Search / Profile Info */}
                        {creatingProfile ? renderTruckInfo() : renderSearchTab()}

                        {/* Service Agreement */}
                        <ServiceAgreementModal
                            key={'serviceAgreement'}
                            useView={true}
                            truckName={foodTruckName || 'Truckster Test Truck'}
                            onDecline={() => goToStep(2)}
                            onAccept={() => goSignUp(selectedTruckID.current)}
                        />

                        {/* Complete Page */}
                        {renderRegisterCompleteView()}
                    </SwipeableViews>
                </View>
            </View>

            {/* Loading View */}
            {isLoading && <LoadingView isLoading={true} isAbsolute={true} description={'Please wait while we sign you up...'} />}
        </View>
    );
    // endregion
}
